// CategoryChips.tsx — Phase 11 Step 4 Block 2 (Routing).
// Sticky Kategorien-Chips oberhalb des Feeds.
// Source-of-truth: Scrolly Platform.html:931–950 (HomeFeed category chips).
//
// Routing-Modell (Block 2):
//   - "Für dich"   → `/`          (Home-Feed, alle Rails handkuratiert)
//   - Kategorie X  → `/category/X` (gefilterter Feed, Block 2 implementiert)
// Aktive Kategorie wird aus `pageContext.urlPathname` abgeleitet — kein
// controlled State mehr. Der Chip ist ein Anchor, Navigation läuft über
// Vike Client-Routing.
//
// A11y:
//   - Kein `role="tablist"` mehr — das war für einen Controlled-Tab-State
//     passend, nicht für Links. Der natürliche a11y-Baum ist nav/list.
//   - Arrow-Tasten-Navigation via focus() auf den Anchor, damit Tastatur-
//     User zwischen Chips wechseln können, ohne jede Chip-Fläche Tab-
//     treffen zu müssen. Die Pfeiltasten-Logik aus Step 3 bleibt erhalten.

import { useCallback, useRef } from "react";
import {
  CATEGORIES,
  VISIBLE_CATEGORIES,
  type CategoryId,
} from "../../lib/categories";
import { VARIANT, type VariantId } from "../../lib/tokens";
import { useVariant } from "../../hooks/useVariant";
import { usePageContext } from "../../hooks/usePageContext";

/**
 * Mappt die aktuelle URL auf die aktive CategoryId.
 *   `/`              → `all`
 *   `/category/:id`  → `:id` (wenn in CATEGORIES; sonst `all`)
 *   sonst            → `null` (keine Kategorie aktiv markieren)
 */
function activeCategoryFromUrl(urlPathname: string): CategoryId | null {
  if (urlPathname === "/" || urlPathname === "") return "all";
  const match = urlPathname.match(/^\/category\/([^/]+)\/?$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]) as CategoryId;
  return CATEGORIES.some((c) => c.id === raw) ? raw : "all";
}

/**
 * Baut die Ziel-URL für einen Chip. "all" geht auf Home `/`, alles andere
 * auf `/category/:id` — diese Trennung entspricht dem Briefing.
 */
function hrefForCategory(id: CategoryId): string {
  return id === "all" ? "/" : `/category/${id}`;
}

export function CategoryChips() {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const pageContext = usePageContext();
  const activeId = activeCategoryFromUrl(pageContext.urlPathname ?? "/");

  const refs = useRef<Array<HTMLAnchorElement | null>>([]);

  const focusAt = useCallback((idx: number) => {
    const wrapped =
      (idx + VISIBLE_CATEGORIES.length) % VISIBLE_CATEGORIES.length;
    const target = refs.current[wrapped];
    if (target) target.focus();
  }, []);

  return (
    <nav
      aria-label="Kategorien"
      style={{
        position: "sticky",
        top: 65,
        zIndex: 30,
        background:
          variant === "immersive"
            ? "rgba(10,10,10,0.92)"
            : "rgba(250,250,247,0.92)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        padding: "14px 32px",
        borderBottom: `1px solid ${v.border}`,
        display: "flex",
        gap: 8,
        overflowX: "auto",
        scrollbarWidth: "none",
      }}
    >
      {VISIBLE_CATEGORIES.map((c, i) => {
        const active = c.id === activeId;
        return (
          <a
            key={c.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            href={hrefForCategory(c.id)}
            aria-current={active ? "page" : undefined}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                focusAt(i + 1);
              } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                focusAt(i - 1);
              } else if (e.key === "Home") {
                e.preventDefault();
                focusAt(0);
              } else if (e.key === "End") {
                e.preventDefault();
                focusAt(VISIBLE_CATEGORIES.length - 1);
              }
            }}
            style={chipStyle(variant, active, v.text)}
          >
            {c.label}
          </a>
        );
      })}
    </nav>
  );
}

function chipStyle(
  variant: VariantId,
  active: boolean,
  text: string,
) {
  return {
    border: "none",
    background: active
      ? variant === "immersive"
        ? "#fff"
        : "#111"
      : variant === "immersive"
        ? "#161616"
        : "#f0efe9",
    color: active
      ? variant === "immersive"
        ? "#000"
        : "#fff"
      : text,
    fontSize: 13,
    fontWeight: 500,
    padding: "7px 14px",
    borderRadius: 999,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    textDecoration: "none" as const,
    display: "inline-block",
    lineHeight: 1.3,
  };
}
